
    import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
    import { TWOFACTOR_RATE_LIMITS, createEmptyOtpDigits } from '@/app/auth/services/twofactor-storage.service'

    const OTP_DIGIT_COUNT = TWOFACTOR_RATE_LIMITS.OTP_DIGIT_COUNT
    const LAST_DIGIT_INDEX = OTP_DIGIT_COUNT - 1

    @Component
    export default class OtpInput extends Vue {
        @Prop({ type: Array, default: () => createEmptyOtpDigits() }) value!: string[]
        @Prop({ type: Boolean, default: false }) hasError!: boolean
        @Prop({ type: String, default: 'otp' }) prefix!: string
        @Prop({ type: Boolean, default: false }) autofocus!: boolean

        digits: string[] = createEmptyOtpDigits()

        @Watch('value', { immediate: true, deep: true })
        onValueChange(newVal: string[]) {
            // Always ensure we have exactly OTP_DIGIT_COUNT digits
            const safeVal = newVal && newVal.length === OTP_DIGIT_COUNT ? newVal : createEmptyOtpDigits()
            this.digits = [...safeVal]
        }

        mounted() {
            if (this.autofocus) {
                this.$nextTick(() => {
                    this.focus()
                })
            }
        }

        /**
         * Get aria-label for a specific digit input (1-indexed for user-friendly labels)
         */
        getDigitAriaLabel(digitNumber: number): string {
            return `Digit ${digitNumber} of ${OTP_DIGIT_COUNT}`
        }

        /**
         * Get aria-label for the input group
         */
        get groupAriaLabel(): string {
            return `${OTP_DIGIT_COUNT}-digit verification code`
        }

        focus() {
            const firstInput = this.$refs.input0 as HTMLInputElement
            if (firstInput) {
                firstInput.focus()
            }
        }

        onInput(event: Event, index: number) {
            const input = event.target as HTMLInputElement
            const value = input.value

            // Only allow digits
            if (value && !/^\d$/.test(value)) {
                input.value = this.digits[index]
                return
            }

            this.digits[index] = value
            this.$emit('input', [...this.digits])

            if (value && index < LAST_DIGIT_INDEX) {
                const nextInput = this.$refs['input' + (index + 1)] as HTMLInputElement
                if (nextInput) {
                    nextInput.focus()
                }
            }
        }

        onKeydown(event: KeyboardEvent, index: number) {
            if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
                const prevInput = this.$refs['input' + (index - 1)] as HTMLInputElement
                if (prevInput) {
                    prevInput.focus()
                }
            } else if (event.key === 'Enter') {
                // Get current input value to check if it's filled
                const currentInput = event.target as HTMLInputElement
                const currentValue = currentInput.value

                // Create a temporary array with the current input's value
                const tempDigits = [...this.digits]
                if (currentValue && /^\d$/.test(currentValue)) {
                    tempDigits[index] = currentValue
                }

                // Check if all digits are filled
                const allFilled = tempDigits.every(digit => digit !== '') && tempDigits.length === OTP_DIGIT_COUNT

                if (allFilled) {
                    event.preventDefault()
                    // Update digits if we found a new value
                    if (currentValue && /^\d$/.test(currentValue) && this.digits[index] !== currentValue) {
                        this.digits[index] = currentValue
                        this.$emit('input', [...this.digits])
                    }
                    this.$emit('submit')
                }
            }
        }

        onPaste(event: ClipboardEvent) {
            event.preventDefault()
            const pastedData = event.clipboardData?.getData('text') || ''
            const pastedDigits = pastedData.replace(/\D/g, '').slice(0, OTP_DIGIT_COUNT).split('')

            pastedDigits.forEach((digit, index) => {
                if (index < OTP_DIGIT_COUNT) {
                    this.digits[index] = digit
                }
            })

            this.$emit('input', [...this.digits])

            // Focus last filled input or first empty
            const lastFilledIndex = Math.min(pastedDigits.length - 1, LAST_DIGIT_INDEX)
            const inputRef = this.$refs['input' + lastFilledIndex] as HTMLInputElement
            if (inputRef) {
                inputRef.focus()
            }
        }

        get isComplete(): boolean {
            return this.digits.every(digit => digit !== '') && this.digits.length === OTP_DIGIT_COUNT
        }

        reset() {
            this.digits = createEmptyOtpDigits()
            this.$emit('input', [...this.digits])
            // Focus first input
            const firstInput = this.$refs.input0 as HTMLInputElement
            if (firstInput) {
                firstInput.focus()
            }
        }
    }
